import type { EnterpriseTask } from "@swot/swot-components";
import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { mapTask } from "../mappers/taskMappers.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { mapCollection } from "./repositoryCollection.js";
import { requireCmtsGuid } from "../utils/cmtsGuid.js";

export type TaskRepository = {
  create(payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  my(tenantId?: string): Promise<RepositoryResult<readonly EnterpriseTask[]>>;
  forComplaint(complaintId: string, tenantId?: string): Promise<RepositoryResult<readonly EnterpriseTask[]>>;
  accept(id: string, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  acknowledge(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  assign(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  complete(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  comment(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  snooze(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
  reopen(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseTask>>;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function readString(payload: Record<string, unknown>, keys: readonly string[], fallback = ""): string {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return fallback;
}

function readUuid(payload: Record<string, unknown>, keys: readonly string[]): string | undefined {
  const value = readString(payload, keys, "");
  return UUID_PATTERN.test(value) ? value : undefined;
}

function readDateIso(payload: Record<string, unknown>, keys: readonly string[]): string | undefined {
  const value = readString(payload, keys, "");
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function normalizeCode(value: string, fallback: string): string {
  const cleaned = value.trim().replace(/[\s-]+/g, "_").toUpperCase();
  return cleaned || fallback;
}

function createClientMutationId(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") return cryptoObj.randomUUID();
  return `cmts-task-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function toCommunicationTaskCreateRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  const parentComplaintId = readUuid(payload, ["parentComplaintId", "complaintId"]);
const linkedEntityId = readUuid(
  payload,
  [
    "linkedEntityId",
    "parentComplaintId",
    "complaintId",
    "sourceEntityId",
  ],
);  const ownerUserId = readUuid(payload, ["ownerUserId", "ownerId"]);
  const assignedToUserId = readUuid(payload, ["assignedToUserId", "assigneeId"]);
  const taskTypeCode = normalizeCode(readString(payload, ["taskTypeCode", "taskType", "type"], "GENERAL"), "GENERAL");
  const priorityCode = normalizeCode(readString(payload, ["priorityCode", "priority"], "MEDIUM"), "MEDIUM");
  const linkedEntityType = readString(payload, ["linkedEntityType", "sourceModule"], linkedEntityId ? "Complaint" : "General");
  const clientMutationId = readString(payload, ["clientMutationId"], createClientMutationId());
  return {
    tenantId,
    title: readString(payload, ["title", "summary", "subject"], "CMTS task"),
    description: readString(payload, ["description", "details"], ""),
    taskTypeCode,
    priorityCode,
    linkedEntityType,
    linkedEntityId,
    parentComplaintId,
    assignedToUserId,
    assignedRoleCode: readString(payload, ["assignedRoleCode", "roleCode"], "") || undefined,
    ownerUserId,
    dueAtUtc: readDateIso(payload, ["dueAtUtc", "dueAt", "dueDate"]),
    confidentiality: normalizeCode(readString(payload, ["confidentiality"], "INTERNAL"), "INTERNAL"),
    idempotencyKey: readString(payload, ["idempotencyKey"], clientMutationId),
    clientMutationId,
    correlationId: readString(payload, ["correlationId"], "") || undefined,
  };
}

function toCommentRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  return {
    tenantId,
    comment: readString(payload, ["comment", "remarks", "reason"], "Task comment"),
    correlationId: readString(payload, ["correlationId"], "") || undefined,
  };
}

function toSnoozeRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  return {
    tenantId,
    reason: readString(payload, ["remarks", "reason", "comment"], "Task snoozed"),
    snoozedUntilUtc: readDateIso(payload, ["snoozeUntilUtc", "snoozedUntilUtc", "snoozeUntil", "dueAt"]),
    correlationId: readString(payload, ["correlationId"], "") || undefined,
  };
}

function toAssignRequest(payload: Record<string, unknown>, tenantId?: string): Record<string, unknown> {
  return {
    tenantId,
    assignedToUserId: readUuid(payload, ["assignedToUserId", "ownerId", "userId"]),
    assignedRoleCode: readString(payload, ["assignedRoleCode", "roleCode"], "") || undefined,
    reason: readString(payload, ["reason", "remarks", "comment"], "Task reassigned"),
  };
}

export function createTaskRepository(client: CmtsHttpClient): TaskRepository {
  const map = async (p: Promise<unknown>) => mapTask(await p);
  const query = (tenantId?: string) => ({ query: { tenantId } });
  return {
create: (
  payload,
  tenantId,
) =>
  repositoryCall(
    () => {
      const complaintId =
        readUuid(
          payload,
          [
            "linkedEntityId",
            "parentComplaintId",
            "complaintId",
          ],
        );

      if (!complaintId) {
        throw new Error(
          "Please select a linked complaint.",
        );
      }

      const assignedToUserId =
        readUuid(
          payload,
          [
            "assignedToUserId",
            "assigneeId",
          ],
        );

      if (!assignedToUserId) {
        throw new Error(
          "Please select a valid user to assign this task.",
        );
      }

      const ownerUserId =
        readUuid(
          payload,
          [
            "ownerUserId",
            "ownerId",
          ],
        ) ??
        assignedToUserId;

      const request =
        toCommunicationTaskCreateRequest(
          {
            ...payload,

            assignedToUserId,

            ownerUserId,

            linkedEntityType:
              "Complaint",

            linkedEntityId:
              complaintId,

            parentComplaintId:
              complaintId,
          },
          tenantId,
        );

      return map(
        client.post<unknown>(
          CMTS_API.tasks
            .createForComplaint(
              complaintId,
            ),
          request,
        ),
      );
    },
    "CMTS_TASK_CREATE",
  ),   my: (tenantId) => repositoryCall(async () => mapCollection(await client.get<unknown>(CMTS_API.tasks.my, query(tenantId)), mapTask), "CMTS_TASKS_MY"),
    forComplaint: (id, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "Task list complaintId");
      return mapCollection(await client.get<unknown>(CMTS_API.complaints.tasks(complaintId), query(tenantId)), mapTask);
    }, "CMTS_TASKS_COMPLAINT"),
    accept: (id, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task accept id");
      return map(client.post<unknown>(CMTS_API.tasks.accept(taskId), undefined, query(tenantId)));
    }, "CMTS_TASK_ACCEPT"),
    acknowledge: (id, _payload, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task acknowledge id");
      return map(client.post<unknown>(CMTS_API.tasks.acknowledge(taskId), undefined, query(tenantId)));
    }, "CMTS_TASK_ACKNOWLEDGE"),
    assign: (id, payload, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task assign id");
      return map(client.post<unknown>(CMTS_API.tasks.assign(taskId), toAssignRequest(payload, tenantId)));
    }, "CMTS_TASK_ASSIGN"),
    complete: (id, payload, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task complete id");
      return map(client.post<unknown>(CMTS_API.tasks.complete(taskId), { tenantId, comment: readString(payload, ["remarks", "comment", "reason"], "Completed from task grid") }));
    }, "CMTS_TASK_COMPLETE"),
    comment: (id, payload, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task comment id");
      return map(client.post<unknown>(CMTS_API.tasks.comment(taskId), toCommentRequest(payload, tenantId), query(tenantId)));
    }, "CMTS_TASK_COMMENT"),
    snooze: (id, payload, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task snooze id");
      return map(client.post<unknown>(CMTS_API.tasks.snooze(taskId), toSnoozeRequest(payload, tenantId), query(tenantId)));
    }, "CMTS_TASK_SNOOZE"),
    reopen: (id, payload, tenantId) => repositoryCall(() => {
      const taskId = requireCmtsGuid(id, "Task reopen id");
      return map(client.post<unknown>(CMTS_API.tasks.reopen(taskId), { tenantId, reason: readString(payload, ["reason", "remarks", "comment"], "Task reopened") }));
    }, "CMTS_TASK_REOPEN"),
  };
}
